import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import OnboardContext from "./context";
import Button from "../common/Button";
import TextBox from "../common/TextBox";
import ComboBox from "../common/ComboBox";
import { langComponent } from "../../constants";
import AppContext from "../../AppContext";

import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import { format, isValid, parse } from "date-fns";
import _, { unset } from "lodash";
import { isValidNumber } from "../../helpers/common.helper";
import useUsers from "../../hooks/useUsers";

const monthOptions = _.range(1, 13).map((_, month) => ({
  name: format(new Date(1900, month, 1), "MMMM"),
  value: (month + 1).toString(),
}));

const dayOptions = _.range(1, 32).map((_) => ({
  name: `${_}`,
  value: _.toString(),
}));

const CommonFieldName = () => {
  const { getLang } = useContext(AppContext);
  const { currentPlan } = useContext(OnboardContext);
  const Translation = getLang(langComponent.onboard);

  if (currentPlan) return <></>;

  return (
    <div className="onboard__form-common-name">
      <TextBox name="firstname" placeholder={Translation("First name")} />
      <TextBox name="lastname" placeholder={Translation("Last name")} />
    </div>
  );
};

const StepNavigationHOC = ({ children }) => {
  return <div className="onboard__form-cta">{children}</div>;
};

const StepNavigationDivider = () => {
  return <div className="onboard__form-cta__divider" />;
};

const GuestForm = () => {
  const { goBack } = useContext(OnboardContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);

  return (
    <>
      <div className="onboard__form-content">
        <CommonFieldName />
        <h4>{Translation("Date of birth")}</h4>
        <div className="onboard__form-birthdate-content">
          <ComboBox name={"day"} placeholder={"Day"} options={dayOptions} />
          <ComboBox
            name={"month"}
            placeholder={"Month"}
            options={monthOptions}
          />
          <TextBox
            name="year"
            placeholder={Translation("Year")}
            width="90"
            sub={"Ex. 1994"}
            type="number"
          />
        </div>
      </div>
      <StepNavigationHOC>
        <Button
          label={TranslationCommon("Return")}
          leadingIcon={<ArrowLeftSvg />}
          onClick={goBack}
          variant="secondary"
        />
        <StepNavigationDivider />
        <Button
          type="submit"
          label={TranslationCommon("Continue")}
          trailingIcon={<ArrowRightSvg />}
        />
      </StepNavigationHOC>
    </>
  );
};

const FreelancerForm = () => {
  const { goBack } = useContext(OnboardContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);

  return (
    <>
      <div className="onboard__form-content">
        <CommonFieldName />
        <h4>{Translation("Date of birth")}</h4>
        <div className="onboard__form-birthdate-content">
          <ComboBox name={"day"} placeholder={"Day"} options={dayOptions} />
          <ComboBox
            name={"month"}
            placeholder={"Month"}
            options={monthOptions}
          />
          <TextBox
            name="year"
            placeholder={"Year"}
            width="90"
            sub="Ex. 1994"
            type="number"
          />
        </div>
        <div className="onboard__form-item">
          <TextBox
            name="taxId"
            placeholder={Translation("Identification No or Tax ID")}
            type="number"
          />
        </div>
        <div className="onboard__form-item">
          <TextBox
            name="experience"
            placeholder={Translation("Years of Experience")}
          />
        </div>
        <div className="onboard__form-item">
          <TextBox name="occupation" placeholder={Translation("Occupation")} />
        </div>
        <div className="onboard__form-item">
          <ComboBox
            name="education"
            placeholder={Translation("Education Level")}
            options={[
              { name: "Elementary", value: "elementary" },
              { name: "High School", value: "highSchool" },
              { name: "College", value: "college" },
              { name: Translation("Bachelor"), value: "bachelor" },
              { name: Translation("Masters"), value: "masters" },
              { name: Translation("Doctoral"), value: "doctoral" },
            ]}
          />
        </div>
      </div>
      <StepNavigationHOC>
        <Button
          label={TranslationCommon("Return")}
          leadingIcon={<ArrowLeftSvg />}
          onClick={goBack}
          variant="secondary"
        />
        <StepNavigationDivider />
        <Button
          type="submit"
          label={TranslationCommon("Continue")}
          trailingIcon={<ArrowRightSvg />}
        />
      </StepNavigationHOC>
    </>
  );
};

const BusinessForm = () => {
  const { goBack } = useContext(OnboardContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);

  return (
    <>
      <div className="onboard__form-content">
        <CommonFieldName />
        <div className="onboard__form-item">
          <TextBox
            name="company"
            placeholder={"Company name"}
            label="Company name"
          />
        </div>
        <div className="onboard__form-item">
          <TextBox
            name="phoneNo"
            placeholder={Translation("Phone no")}
            type="number"
            label="Phone no"
          />
        </div>
        <div className="onboard__form-item">
          <TextBox
            name="taxId"
            placeholder={Translation("Tax ID")}
            type="number"
            label="Tax ID"
          />
        </div>
        <div className="onboard__form-item">
          <TextBox
            name="experience"
            type="number"
            placeholder={Translation("Years of Experience")}
            label="Experience"
          />
        </div>
        <div className="onboard__form-item">
          <TextBox
            name="services"
            placeholder={Translation("What services do you provide?")}
            label="Services"
          />
        </div>
      </div>
      <StepNavigationHOC>
        <Button
          label={TranslationCommon("Return")}
          leadingIcon={<ArrowLeftSvg />}
          onClick={goBack}
          variant="secondary"
        />
        <StepNavigationDivider />
        <Button
          type="submit"
          label={TranslationCommon("Continue")}
          trailingIcon={<ArrowRightSvg />}
        />
      </StepNavigationHOC>
    </>
  );
};

const Info = () => {
  const {
    selectedPlan: { name: planName },
    information,
    addInformation,
    goForward,
  } = useContext(OnboardContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);
  const { queryCompanies } = useUsers();

  // * Get form to display
  const getForm = () => {
    if (planName === "Guest") {
      return <GuestForm />;
    }

    if (planName === "Freelancer" || planName === "Trial") {
      return <FreelancerForm />;
    }

    if (planName === "Business") {
      return <BusinessForm />;
    }
  };

  const handleValidate = ({
    firstname,
    lastname,
    studentId,
    taxId,
    university,
    major,
    degree,
    occupation,
    experience,
    education,
    company,
    services,
    phoneNo,
    day,
    month,
    year,
    startingYear,
    endingYear,
  }) => {
    let errors = {};

    const validateDateOfBirth = () => {
      const currentYear = new Date().getFullYear();

      if (!day) errors.day = TranslationCommon("Invalid day");
      if (month === null || month === undefined)
        errors.month = TranslationCommon("Invalid month");
      if (!year) errors.year = TranslationCommon("Invalid year");

      // * Check Nan (Not an number)
      if (isNaN(day)) errors.day = TranslationCommon("Invalid day");
      if (isNaN(month)) errors.month = TranslationCommon("Invalid month");
      if (isNaN(year)) errors.year = TranslationCommon("Invalid year");

      if (day && (+day < 1 || +day > 31))
        errors.day = TranslationCommon("Invalid day");
      if (month && (+month < 1 || +month > 12))
        errors.month = TranslationCommon("Invalid month");
      if (year && (+year < currentYear - 100 || +year > currentYear))
        errors.year = TranslationCommon("Invalid year");

      // * Check if date is valid
      const parsed = parse(
        `${+day}/${+month}/${+year}`,
        "d/M/yyyy",
        new Date()
      );

      if (!isValid(parsed)) {
        errors.day = TranslationCommon("Invalid day");
        errors.month = TranslationCommon("Invalid month");
        errors.year = TranslationCommon("Invalid year");
      }

      // * Check date if no errors yet
      // TODO: Check allowed age
      if (
        !errors.day &&
        !errors.month &&
        !errors.year &&
        new Date() <= new Date(+year, +month - 1, +day)
      ) {
        errors.day = TranslationCommon("Invalid day");
        errors.month = TranslationCommon("Invalid month");
        errors.year = TranslationCommon("Invalid year");
      }
    };

    if (planName === "Guest") {
      if (!firstname) errors.firstname = TranslationCommon("Required");
      if (!lastname) errors.lastname = TranslationCommon("Required");
      validateDateOfBirth();
    }

    if (planName === "Trial") {
      if (!firstname) errors.firstname = TranslationCommon("Required");
      if (!lastname) errors.lastname = TranslationCommon("Required");
      if (!taxId) errors.taxId = TranslationCommon("Required");
      if (experience && !isValidNumber(experience))
        errors.experience = TranslationCommon("Required");
      if (!occupation) errors.occupation = TranslationCommon("Required");
      if (!education) errors.education = TranslationCommon("Required");
      validateDateOfBirth();
    }

    if (planName === "Freelancer") {
      if (!firstname) errors.firstname = TranslationCommon("Required");
      if (!lastname) errors.lastname = TranslationCommon("Required");
      if (!taxId) errors.taxId = TranslationCommon("Required");
      if (!experience) errors.experience = TranslationCommon("Required");
      if (!isValidNumber(experience))
        errors.experience = TranslationCommon("Required");
      if (!occupation) errors.occupation = TranslationCommon("Required");
      if (!education) errors.education = TranslationCommon("Required");
      validateDateOfBirth();
    }

    if (planName === "Business") {
      if (!firstname) errors.firstname = TranslationCommon("Required");
      if (!lastname) errors.lastname = TranslationCommon("Required");
      if (!company) errors.company = TranslationCommon("Required");
      if (!taxId) errors.taxId = TranslationCommon("Required");
      if (taxId && !Number(taxId)) errors.taxId = "Can only contain numbers";
      if (!isValidNumber(experience))
        errors.experience = TranslationCommon("Required");
      if (!services) errors.services = TranslationCommon("Required");
      if (!phoneNo) errors.phoneNo = TranslationCommon("Required");
      if (phoneNo && !Number(phoneNo))
        errors.phoneNo = "Can only contain numbers";
    }

    return errors;
  };

  const handleSubmit = async (values, options) => {
    if (!values.company) {
      addInformation({ info: values });
      goForward();
      return;
    }

    // * Check if company exists
    const { data: _companiesFound } = await queryCompanies(
      {
        name: values.company,
      },
      1,
      1
    );

    if (_companiesFound.length > 0) {
      options.setFieldError("company", "Company with same name already exists");
      return;
    }

    addInformation({ info: values });
    goForward();
  };

  return (
    <div className="onboard-info">
      <h1 className="onboard__header">{Translation("Information")}</h1>
      <Formik
        initialValues={
          information?.info || {
            firstname: "",
            lastname: "",
            studentId: "",
            taxId: "",
            university: "",
            major: "",
            degree: "",
            occupation: "",
            experience: "",
            education: "",
            company: "",
            services: [],
            phoneNo: "",
            startingYear: "",
            endingYear: "",
          }
        }
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        <Form className="onboard__form">{getForm()}</Form>
      </Formik>
    </div>
  );
};

Info.propTypes = {};

export default Info;
